import { useTranslation } from 'react-i18next';
/**
 * ColumnFilter — Reusable inline column filter for data tables.
 *
 * Provides:
 *  - useColumnFilters hook: manages filter state and applies filters to features
 *  - FilterHeaderCell: renders a <th> with an inline search input for string columns
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../utils/cn';

/* ── Hook ── */

export interface ColumnFilters {
  [columnName: string]: string;
}

/**
 * Hook that manages column filter state and returns filtered features.
 * Stats/aggregations should be computed on the UNFILTERED data.
 * Pagination should be applied AFTER filtering.
 */
export function useColumnFilters<T extends Record<string, any>>(
  features: T[],
  keys: string[],
) {
  const [filters, setFilters] = useState<ColumnFilters>({});

  const setFilter = useCallback((col: string, value: string) => {
    setFilters((prev) => {
      if (!value) {
        const next = { ...prev };
        delete next[col];
        return next;
      }
      return { ...prev, [col]: value };
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const hasActiveFilters = Object.keys(filters).length > 0;

  const filteredFeatures = useMemo(() => {
    const activeFilters = Object.entries(filters).filter(([, v]) => v.length > 0);
    if (activeFilters.length === 0) return features;

    return features.filter((feat) => {
      return activeFilters.every(([col, search]) => {
        const val = feat[col] ?? feat?.properties?.[col];
        if (val == null) return false;
        return String(val).toLowerCase().includes(search.toLowerCase());
      });
    });
  }, [features, filters]);

  /** Detect which columns are string (non-numeric) */
  const stringColumns = useMemo(() => {
    const strCols = new Set<string>();
    const sample = features.slice(0, 50); // check first 50 rows
    for (const key of keys) {
      const hasString = sample.some((f) => {
        const v = f[key] ?? f?.properties?.[key];
        if (v == null) return false;
        const s = String(v).replace(/[%,\s]/g, '').trim();
        return s !== '' && isNaN(Number(s));
      });
      if (hasString) strCols.add(key);
    }
    return strCols;
  }, [features, keys]);

  return { filters, setFilter, clearFilters, hasActiveFilters, filteredFeatures, stringColumns };
}

/* ── Filter Header Cell ── */

interface FilterHeaderCellProps {
  columnName: string;
  isFilterable: boolean;
  filterValue: string;
  onFilterChange: (col: string, value: string) => void;
  className?: string;
  children?: React.ReactNode;
}

export const FilterHeaderCell: React.FC<FilterHeaderCellProps> = ({
  columnName,
  isFilterable,
  filterValue,
  onFilterChange,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const [showInput, setShowInput] = useState(false);
  const isActive = filterValue.length > 0;

  return (
    <th className={cn('relative group', className)}>
      <div className="flex items-center gap-1">
        <span className="truncate">{children ?? columnName}</span>
        {isFilterable && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowInput((v) => !v); }}
            className={cn(
              'shrink-0 p-0.5 rounded transition-colors',
              isActive
                ? 'text-gn-blue bg-gn-blue-light'
                : 'text-gn-muted opacity-0 group-hover:opacity-100 hover:text-gn-primary',
            )}
            title={`Filter ${columnName}`}
          >
            <Search size={9} />
          </button>
        )}
      </div>
      {isFilterable && (showInput || isActive) && (
        <div className="mt-0.5 flex items-center gap-0.5">
          <input
            autoFocus
            type="text"
            value={filterValue}
            onChange={(e) => onFilterChange(columnName, e.target.value)}
            placeholder={t('table.filter')}
            onClick={(e) => e.stopPropagation()}
            className="w-full px-1 py-0.5 text-[9px] bg-gn-tertiary border border-gn rounded text-gn-primary placeholder-gn-muted focus:border-gn-blue focus:outline-none"
          />
          {isActive && (
            <button
              onClick={(e) => { e.stopPropagation(); onFilterChange(columnName, ''); setShowInput(false); }}
              className="shrink-0 text-gn-secondary hover:text-gn-primary"
            >
              <X size={9} />
            </button>
          )}
        </div>
      )}
    </th>
  );
};
